import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";

const { SalaryIncrement } = config.REST_API;

/** Matches backend SalaryIncrementStatus */
export const IncrementStatus = {
  Pending: 1,
  OnHold: 2,
  Active: 3,
};

/** Matches backend SalaryIncrementSource */
export const IncrementSource = {
  Manual: 0,
  Appraisal: 1,
};

const omitEmptyParams = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
};

const fetchExportBlob = async (path, query) => {
  const res = await axiosBasic.get(path, {
    params: omitEmptyParams(query),
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      if (json && json.success === false) {
        throw new Error(json.message || "Export failed");
      }
    } catch (e) {
      if (e instanceof Error && e.message !== "Export failed") throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

export function normalizeIncrement(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return {
    id: String(get("id", "Id") ?? ""),
    userId: String(get("userId", "UserId") ?? ""),
    employeeName: String(get("employeeName", "EmployeeName") ?? ""),
    designation: String(get("designation", "Designation") ?? ""),
    email: String(get("email", "Email") ?? ""),
    imageURL: get("imageURL", "ImageURL") ?? null,
    previousGrossSalary: Number(get("previousGrossSalary", "PreviousGrossSalary") ?? 0),
    newGrossSalary: Number(get("newGrossSalary", "NewGrossSalary") ?? 0),
    incrementAmount: Number(get("incrementAmount", "IncrementAmount") ?? 0),
    effectiveFrom: get("effectiveFrom", "EffectiveFrom") ?? "",
    status: Number(get("status", "Status") ?? IncrementStatus.Pending),
    statusLabel: String(get("statusLabel", "StatusLabel") ?? ""),
    source: Number(get("source", "Source") ?? IncrementSource.Manual),
    sourceLabel: String(get("sourceLabel", "SourceLabel") ?? ""),
    userAppraisalId: get("userAppraisalId", "UserAppraisalId")
      ? String(get("userAppraisalId", "UserAppraisalId"))
      : "",
    reason: String(get("reason", "Reason") ?? ""),
    createdDate: get("createdDate", "CreatedDate") ?? "",
    updatedDate: get("updatedDate", "UpdatedDate") ?? "",
  };
}

export function unwrapPaginatedList(res) {
  const body = res?.data;
  if (body == null || body.success === false) {
    return { items: [], totalCount: 0, page: 1, pageSize: 20, message: body?.message };
  }
  const data = body.data != null ? body.data : body;
  let items = [];
  if (Array.isArray(data?.items)) items = data.items;
  else if (Array.isArray(data?.Items)) items = data.Items;
  else if (Array.isArray(data)) items = data;

  const totalCount = Number(
    data?.totalCount ?? data?.TotalCount ?? body?.total ?? body?.Total ?? items.length ?? 0
  );

  return {
    items: items.map(normalizeIncrement),
    totalCount,
    page: Number(data?.page ?? data?.Page ?? 1),
    pageSize: Number(data?.pageSize ?? data?.PageSize ?? 20),
    appliedYear: data?.appliedYear ?? data?.AppliedYear ?? null,
    message: null,
  };
}

export function unwrapOne(res) {
  const body = res?.data;
  if (body == null || body.success === false) return { item: null, message: body?.message };
  const raw = body.data != null ? body.data : body;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const hasId = raw.id != null || raw.Id != null;
    const hasUserId = raw.userId != null || raw.UserId != null;
    if (hasId || hasUserId) {
      return { item: normalizeIncrement(raw), message: null };
    }
  }
  return { item: null, message: body?.message };
}

export function normalizeIncrementEmployeeGridRow(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const prevRaw = get("previousGrossSalary", "PreviousGrossSalary");
  const defaultRaw = get("defaultGrossSalary", "DefaultGrossSalary");
  const existingGrossRaw = get("existingIncrementGrossSalary", "ExistingIncrementGrossSalary");
  const parseOptionalMoney = (v) =>
    v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null;
  const parseOptionalDate = (v) => {
    if (v == null || v === "") return null;
    const s = String(v);
    if (s.length >= 10) return s.slice(0, 10);
    return s;
  };
  return {
    userId: String(get("userId", "UserId") ?? ""),
    employeeName: String(get("employeeName", "EmployeeName") ?? ""),
    designationName: String(get("designationName", "DesignationName") ?? ""),
    departmentName: String(get("departmentName", "DepartmentName") ?? ""),
    workplaceName: String(get("workplaceName", "WorkplaceName") ?? ""),
    imageURL: get("imageURL", "ImageURL") ?? null,
    previousGrossSalary: parseOptionalMoney(prevRaw),
    defaultGrossSalary: parseOptionalMoney(defaultRaw),
    hasIncrementOnSelectedDate: Boolean(get("hasIncrementOnSelectedDate", "HasIncrementOnSelectedDate")),
    existingIncrementId: String(get("existingIncrementId", "ExistingIncrementId") ?? ""),
    existingIncrementGrossSalary: parseOptionalMoney(existingGrossRaw),
    canScheduleIncrement: get("canScheduleIncrement", "CanScheduleIncrement") !== false,
    nextIncrementEffectiveFrom: parseOptionalDate(get("nextIncrementEffectiveFrom", "NextIncrementEffectiveFrom")),
    nextAvailableEffectiveFrom: parseOptionalDate(get("nextAvailableEffectiveFrom", "NextAvailableEffectiveFrom")),
  };
}

/**
 * @param {object} filters
 * @param {number} [filters.page]
 * @param {number} [filters.pageSize]
 * @param {string} [filters.search]
 * @param {number|string} [filters.year]
 * @param {string} [filters.status]
 * @param {number|string} [filters.departmentId]
 * @param {number|string} [filters.officeId]
 */
export const buildListQuery = (filters) => {
  const q = {};
  if (filters.page != null) q.page = Number(filters.page);
  if (filters.pageSize != null) q.pageSize = Number(filters.pageSize);
  const search = String(filters.search ?? "").trim();
  if (search) q.search = search;
  if (filters.year != null && filters.year !== "" && filters.year !== "all") {
    q.year = Number(filters.year);
  }
  if (filters.status != null && filters.status !== "" && filters.status !== "all") {
    q.status = statusToApiPayload(filters.status);
  }
  if (filters.departmentId != null && filters.departmentId !== "" && filters.departmentId !== "all") {
    q.departmentId = Number(filters.departmentId);
  }
  if (filters.officeId != null && filters.officeId !== "" && filters.officeId !== "all") {
    q.officeId = Number(filters.officeId);
  }
  return q;
};

export const buildExportQuery = (filters) => {
  const q = buildListQuery(filters);
  delete q.page;
  delete q.pageSize;
  return q;
};

export const fetchSalaryIncrements = (query) =>
  axiosBasic.get(SalaryIncrement.Get, { params: buildListQuery(query) });

export const fetchIncrementEmployeeGrid = ({ search, effectiveFrom, excludeIncrementId } = {}) => {
  const q = {};
  const trimmed = String(search ?? "").trim();
  if (trimmed) q.search = trimmed;
  if (effectiveFrom) q.effectiveFrom = String(effectiveFrom).slice(0, 10);
  if (excludeIncrementId) q.excludeIncrementId = String(excludeIncrementId);
  return axiosBasic.get(SalaryIncrement.EmployeesGrid, { params: omitEmptyParams(q) });
};

export function unwrapEmployeeGridList(res) {
  const body = res?.data;
  if (body == null || body.success === false) {
    return { items: [], message: body?.message };
  }
  const data = body.data != null ? body.data : body;
  const raw =
    Array.isArray(data?.items) ? data.items
    : Array.isArray(data?.Items) ? data.Items
    : Array.isArray(data) ? data
    : [];
  return { items: raw.map(normalizeIncrementEmployeeGridRow), message: null };
}

export const createSalaryIncrement = (payload) => axiosBasic.post(SalaryIncrement.Post, payload);

export const updateSalaryIncrement = (payload) => axiosBasic.put(SalaryIncrement.Put, payload);

export const updateSalaryIncrementStatus = (payload) =>
  axiosBasic.put(SalaryIncrement.UpdateStatus, {
    ...payload,
    status: statusToApiPayload(payload?.status),
  });

export function statusToApiPayload(status) {
  const s = String(status ?? "").trim();
  if (!s) return s;
  const normalized = s.toLowerCase().replace(/\s+/g, "");
  if (normalized === "onhold") return "OnHold";
  if (normalized === "pending") return "Pending";
  if (normalized === "active") return "Active";
  return s;
}

export const fetchSalaryIncrementExportExcel = (query) =>
  fetchExportBlob(SalaryIncrement.ExportExcel, buildExportQuery(query));

export const fetchSalaryIncrementExportCsv = (query) =>
  fetchExportBlob(SalaryIncrement.ExportCsv, buildExportQuery(query));

export const fetchSalaryIncrementExportPdf = (query) =>
  fetchExportBlob(SalaryIncrement.ExportPdf, buildExportQuery(query));

export const salaryIncrementExportBasename = () => {
  const now = new Date();
  const stamp =
    String(now.getFullYear()) +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0") +
    String(now.getHours()).padStart(2, "0") +
    String(now.getMinutes()).padStart(2, "0");
  return `salary-increments_${stamp}`;
};

export function formatIncrementMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
}

export function formatEffectiveDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function toDateInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayDateInputValue() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isEffectiveDateInPast(dateInputValue) {
  const s = String(dateInputValue ?? "").trim();
  if (!s) return false;
  return s < todayDateInputValue();
}

export function isEffectiveDateReached(effectiveFromIso) {
  if (!effectiveFromIso) return false;
  const eff = new Date(effectiveFromIso);
  if (Number.isNaN(eff.getTime())) return false;
  const today = new Date();
  const effDate = new Date(Date.UTC(eff.getUTCFullYear(), eff.getUTCMonth(), eff.getUTCDate()));
  const todayDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  return effDate.getTime() <= todayDate.getTime();
}

export function statusBadgeClass(statusLabel) {
  const label = String(statusLabel ?? "").toLowerCase();
  if (label === "active") return "payroll-badge--active";
  if (label.includes("hold")) return "payroll-badge--draft";
  return "payroll-badge--pending";
}

export function sourceBadgeClass(sourceOrLabel) {
  const n = Number(sourceOrLabel);
  if (n === IncrementSource.Appraisal) return "payroll-badge--appraisal";
  const label = String(sourceOrLabel ?? "").toLowerCase();
  if (label.includes("appraisal")) return "payroll-badge--appraisal";
  return "payroll-badge--draft";
}

export function formatSourceLabel(row, t) {
  const source = Number(row?.source);
  if (source === IncrementSource.Appraisal || String(row?.sourceLabel ?? "").toLowerCase().includes("appraisal")) {
    return t ? t("app.payroll.increments.sourceAppraisal", { defaultValue: "Appraisal" }) : "Appraisal";
  }
  if (row?.sourceLabel) return row.sourceLabel;
  return t ? t("app.payroll.increments.sourceManual", { defaultValue: "Manual" }) : "Manual";
}

export function statusApiValue(statusLabel) {
  const label = String(statusLabel ?? "").toLowerCase();
  if (label === "active") return "Active";
  if (label.includes("hold")) return "On Hold";
  return "Pending";
}
